/*
 * Citera — global.js
 * Small, dependency-free UI layer: modal drawers (mobile nav + cart) and an
 * AJAX cart backed by Shopify's Cart + Section Rendering APIs. Deferred, no framework.
 */
(function () {
  "use strict";

  var CART_DRAWER_ID = "cart-drawer"; // the <dialog> + the section name requested
  var CART_CONTENT_ID = "cart-drawer-content"; // inner region swapped on every cart change

  /* ---------- Accessibility helpers ---------- */
  var strings = window.CiteraStrings || {};
  // Polite live region for status messages (cart updates, errors).
  var liveRegion;
  function announce(message) {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement("p");
      liveRegion.className = "visually-hidden";
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", "polite");
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = "";
    window.setTimeout(function () {
      liveRegion.textContent = message;
    }, 60);
  }

  /* ---------- Drawers (native <dialog>) ---------- */
  // Remember what opened each dialog so focus returns there on close (WCAG 2.4.3).
  var dialogInvoker = new WeakMap();

  function openDialog(dialog, invoker) {
    if (!dialog || dialog.open) return;
    dialogInvoker.set(dialog, invoker || document.activeElement);
    // The facet drawer is inline filters (role=group) on desktop; it is a real
    // modal dialog only while open on mobile.
    if (dialog.id === "facet-drawer") dialog.setAttribute("role", "dialog");
    try {
      dialog.showModal();
    } catch (e) {
      // iOS Safari can throw on showModal() (e.g. dialog under a transformed/
      // sticky ancestor). Fall back to a non-modal open so the drawer still shows.
      dialog.setAttribute("open", "");
      dialog.classList.add("dialog--fallback");
    }
    document.documentElement.style.overflow = "hidden";
    var focusable = dialog.querySelector(
      "[autofocus], [data-drawer-close], [data-lightbox-close]",
    );
    if (focusable) focusable.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }

  document.addEventListener("click", function (event) {
    var opener = event.target.closest("[data-drawer-open]");
    if (opener) {
      event.preventDefault();
      var target = opener.getAttribute("data-drawer-open");
      openDialog(document.getElementById(target), opener);
      if (target === CART_DRAWER_ID) loadCartUpsell();
      return;
    }
    var closer = event.target.closest("[data-drawer-close]");
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest("dialog"));
    }
  });

  // Restore scroll + return focus whenever any dialog closes; backdrop click closes.
  document.querySelectorAll("dialog").forEach(function (dialog) {
    dialog.addEventListener("close", function () {
      document.documentElement.style.overflow = "";
      if (dialog.id === "facet-drawer") dialog.removeAttribute("role");
      var invoker = dialogInvoker.get(dialog);
      dialogInvoker.delete(dialog);
      if (invoker && typeof invoker.focus === "function" && document.contains(invoker)) {
        invoker.focus();
      }
    });
    dialog.addEventListener("click", function (event) {
      if (event.target === dialog) closeDialog(dialog);
    });
  });

  /* ---------- Predictive search ---------- */
  (function () {
    var roots = document.querySelectorAll("[data-predictive-root]");
    if (!roots.length) return;

    var cache = new Map();
    var strings = window.CiteraStrings || {};

    roots.forEach(function (root) {
      var input = root.querySelector("[data-predictive-input]");
      var results = root.querySelector("[data-predictive-results]");
      var status = root.querySelector("[data-predictive-status]");
      if (!input || !results) return;

      var endpoint =
        input.getAttribute("data-predictive-url") ||
        window.Shopify.routes.root + "search/suggest";
      var timer;
      var controller;
      var requestTerm = "";

      function setExpanded(open) {
        input.setAttribute("aria-expanded", open ? "true" : "false");
        root.classList.toggle("is-predictive-open", open);
      }

      function closeResults() {
        if (controller) controller.abort();
        clearTimeout(timer);
        results.innerHTML = "";
        root.classList.remove("is-predictive-loading");
        setExpanded(false);
        if (status) status.textContent = "";
      }

      function updateResults(html, term) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var section = doc.querySelector("#shopify-section-predictive-search");
        results.innerHTML = section ? section.innerHTML : "";
        root.classList.remove("is-predictive-loading");

        // Product, collection and query links are the useful search choices.
        var count = results.querySelectorAll(
          ".predictive__product, .predictive__collection, .predictive__query",
        ).length;
        setExpanded(results.children.length > 0);
        if (status) {
          var tpl =
            count === 1
              ? strings.searchResultsOne || "[count] result"
              : strings.searchResultsOther || "[count] results";
          status.textContent = tpl.replace("[count]", count);
        }
        requestTerm = term;
      }

      function render(term) {
        if (term.length < 2) {
          closeResults();
          return;
        }

        if (cache.has(term)) {
          updateResults(cache.get(term), term);
          return;
        }

        if (controller) controller.abort();
        controller = new AbortController();
        requestTerm = term;
        root.classList.add("is-predictive-loading");
        results.innerHTML =
          '<div class="predictive__loading" role="presentation"><span></span><span></span><span></span></div>';
        setExpanded(true);
        if (status) status.textContent = strings.searchLoading || "Searching";

        var url =
          endpoint +
          "?q=" +
          encodeURIComponent(term) +
          "&section_id=predictive-search" +
          "&resources[type]=product,collection,query&resources[limit]=6";

        fetch(url, { signal: controller.signal })
          .then(function (response) {
            if (!response.ok) throw new Error("Predictive search request failed");
            return response.text();
          })
          .then(function (html) {
            if (input.value.trim() !== term || requestTerm !== term) return;
            cache.set(term, html);
            if (cache.size > 12) cache.delete(cache.keys().next().value);
            updateResults(html, term);
          })
          .catch(function (error) {
            if (error.name === "AbortError") return;
            root.classList.remove("is-predictive-loading");
            results.innerHTML = "";
            setExpanded(false);
            if (status) {
              status.textContent = strings.searchUnavailable || "Search is temporarily unavailable";
            }
          });
      }

      input.addEventListener("input", function () {
        var term = input.value.trim();
        clearTimeout(timer);
        timer = window.setTimeout(function () {
          render(term);
        }, 220);
      });

      input.addEventListener("focus", function () {
        var term = input.value.trim();
        if (term.length >= 2 && (!results.children.length || requestTerm !== term)) {
          render(term);
        } else if (results.children.length) {
          setExpanded(true);
        }
      });

      input.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && results.children.length) {
          event.preventDefault();
          closeResults();
          return;
        }
        if (event.key !== "ArrowDown") return;
        var firstLink = results.querySelector("a[href]");
        if (firstLink) {
          event.preventDefault();
          firstLink.focus();
        }
      });

      results.addEventListener("keydown", function (event) {
        if (!["ArrowDown", "ArrowUp", "Escape"].includes(event.key)) return;
        var links = Array.from(results.querySelectorAll("a[href]"));
        var current = links.indexOf(document.activeElement);
        if (event.key === "Escape") {
          event.preventDefault();
          input.focus();
          closeResults();
          return;
        }
        if (!links.length || current === -1) return;
        event.preventDefault();
        var next = event.key === "ArrowDown" ? current + 1 : current - 1;
        if (next < 0) {
          input.focus();
        } else {
          links[Math.min(next, links.length - 1)].focus();
        }
      });

      document.addEventListener("click", function (event) {
        if (!root.contains(event.target)) closeResults();
      });

      var dialog = root.closest("dialog");
      if (dialog) dialog.addEventListener("close", closeResults);
    });
  })();

  /* ---------- AJAX cart ---------- */
  function renderCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function refreshCartDrawer(sectionsHTML) {
    var current = document.getElementById(CART_CONTENT_ID);
    var html = sectionsHTML && sectionsHTML[CART_DRAWER_ID];
    if (current && html) {
      var parsed = new DOMParser().parseFromString(html, "text/html");
      var nextCount = parsed.querySelector("[data-cart-count]");
      if (nextCount) renderCartCount(Number(nextCount.textContent.trim()) || 0);
      var next = parsed.getElementById(CART_CONTENT_ID);
      if (next) current.replaceWith(next); // swap inner region only, keep <dialog> open
    }
    updateCartCount();
    loadCartUpsell();
  }

  // In-cart upsell: fetch complementary recommendations for the first line item.
  function loadCartUpsell() {
    var box = document.querySelector("[data-cart-upsell]");
    if (!box) return;
    var pid = box.getAttribute("data-product-id");
    if (!pid) {
      box.innerHTML = "";
      return;
    }
    fetch(
      window.Shopify.routes.root +
        "recommendations/products?product_id=" +
        pid +
        "&limit=2&intent=complementary&section_id=cart-upsell",
    )
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var section = doc.querySelector(".shopify-section");
        box.innerHTML = section ? section.innerHTML : "";
      })
      .catch(function () {});
  }

  // Cart note + gift attribute (persist without re-rendering the open drawer).
  function updateCartField(body) {
    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }
  document.addEventListener("change", function (event) {
    var note = event.target.closest("[data-cart-note]");
    if (note) {
      updateCartField({ note: note.value });
      return;
    }
    var gift = event.target.closest("[data-cart-gift]");
    if (gift) {
      updateCartField({ attributes: { Gift: gift.checked ? "Yes" : "" } });
    }
  });

  function updateCartCount() {
    fetch(window.Shopify.routes.root + "cart.js")
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        renderCartCount(cart.item_count);
      });
  }

  function postCart(endpoint, body) {
    body.sections = [CART_DRAWER_ID];
    body.sections_url = window.location.pathname;
    return fetch(window.Shopify.routes.root + endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  // Inline cart error next to a form + a polite announcement.
  function showCartError(form, message) {
    var text = message || strings.cartError || "Sorry, this item couldn't be added to your cart.";
    var box = form.querySelector("[data-cart-error]");
    if (!box) {
      box = document.createElement("p");
      box.setAttribute("data-cart-error", "");
      box.setAttribute("role", "alert");
      box.className = "cart-error";
      form.appendChild(box);
    }
    box.textContent = text;
    announce(text);
  }
  function clearCartError(form) {
    var box = form.querySelector("[data-cart-error]");
    if (box) box.remove();
  }

  // Build a cart item from the full form: id + quantity + selling_plan +
  // properties[...] (gift-card recipient etc). Dropping these silently was a
  // real bug — the payload must mirror what a native (non-AJAX) submit sends.
  function formToItem(formData) {
    var item = {
      id: formData.get("id"),
      quantity: Number(formData.get("quantity") || 1),
    };
    var plan = formData.get("selling_plan");
    if (plan) item.selling_plan = Number(plan);
    var props = {};
    formData.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m && value !== "") props[m[1]] = value;
    });
    if (Object.keys(props).length) item.properties = props;
    return item;
  }

  // Intercept add-to-cart forms so the drawer opens instead of a full page load.
  document.addEventListener("submit", function (event) {
    var form = event.target.closest("form[data-cart-form]");
    if (!form) return;
    event.preventDefault();
    var product = form.closest("[data-product]");
    if (product && requestVariantSelection(product)) return;
    var button = form.querySelector('[type="submit"]');
    var variantInput = form.querySelector("[data-variant-id]");
    if (product && ((variantInput && variantInput.disabled) || (button && button.disabled))) return;
    if (button) button.setAttribute("aria-busy", "true");
    clearCartError(form);

    var formData = new FormData(form);
    var body = { items: [formToItem(formData)] };
    postCart("cart/add.js", body)
      .then(function (data) {
        if (data && (data.status || data.description)) {
          showCartError(form, data.description || data.message);
          return;
        }
        refreshCartDrawer(data.sections);
        openDialog(document.getElementById(CART_DRAWER_ID), button);
        announce(strings.cartAdded || "Added to cart");
      })
      .catch(function () {
        showCartError(form);
      })
      .finally(function () {
        if (button) button.removeAttribute("aria-busy");
      });
  });

  // Quantity change + remove inside the drawer (delegated).
  document.addEventListener("change", function (event) {
    var input = event.target.closest("[data-cart-quantity]");
    if (!input) return;
    postCart("cart/change.js", {
      line: Number(input.getAttribute("data-line")),
      quantity: Number(input.value),
    })
      .then(function (data) {
        if (data && data.status) {
          announce(data.description || data.message || strings.cartError);
          return;
        }
        refreshCartDrawer(data.sections);
        announce(strings.cartUpdated || "Cart updated");
      })
      .catch(function () {
        announce(strings.cartError || "Couldn't update the cart");
      });
  });

  document.addEventListener("click", function (event) {
    var remove = event.target.closest("[data-cart-remove]");
    if (!remove) return;
    event.preventDefault();
    postCart("cart/change.js", {
      line: Number(remove.getAttribute("data-line")),
      quantity: 0,
    })
      .then(function (data) {
        if (data && data.status) {
          announce(data.description || data.message || strings.cartError);
          return;
        }
        refreshCartDrawer(data.sections);
        announce(strings.cartUpdated || "Cart updated");
      })
      .catch(function () {
        announce(strings.cartError || "Couldn't update the cart");
      });
  });

  // Stepper +/- buttons: adjust the sibling quantity input, then fire its change.
  document.addEventListener("click", function (event) {
    var step = event.target.closest("[data-qty-step]");
    if (!step) return;
    var input = step.parentElement.querySelector("[data-cart-quantity]");
    if (!input) return;
    var next = Math.max(0, Number(input.value) + Number(step.getAttribute("data-qty-step")));
    input.value = next;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---------- Product: variant selection + media gallery ---------- */
  // Romanian public holidays (non-working days). Extend this list every year.
  var nonWorkingDays = [
    "2026-11-30", "2026-12-01", "2026-12-25", "2026-12-26",
    "2027-01-01", "2027-01-02", "2027-01-06", "2027-01-07", "2027-01-24",
    "2027-04-30", "2027-05-01", "2027-05-02", "2027-05-03", "2027-06-01",
    "2027-06-20", "2027-06-21", "2027-08-15", "2027-11-30", "2027-12-01",
    "2027-12-25", "2027-12-26",
  ];

  function isBusinessDay(date) {
    var day = date.getDay();
    if (day === 0 || day === 6) return false;
    var key =
      date.getFullYear() +
      "-" + String(date.getMonth() + 1).padStart(2, "0") +
      "-" + String(date.getDate()).padStart(2, "0");
    return nonWorkingDays.indexOf(key) === -1;
  }

  function addBusinessDays(date, days) {
    var result = new Date(date.getTime());
    var remaining = days;
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      if (isBusinessDay(result)) remaining -= 1;
    }
    return result;
  }

  function updateDeliveryWindow(element) {
    if (!element) return;
    var raw = element.dataset.orderDate || "";
    var parts = raw.split("-").map(Number);
    if (parts.length !== 3 || parts.some(function (value) { return !value; })) return;

    // Noon avoids date drift around DST/time-zone boundaries. The source date
    // is rendered by Shopify in the store timezone.
    var orderDate = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
    // Delivery = dispatch in 1–2 business days (24–48 working hours) + 1 business day with the courier.
    var firstDate = addBusinessDays(orderDate, 2);
    var secondDate = addBusinessDays(orderDate, 3);
    var output = element.querySelector("[data-delivery-dates]");
    if (!output) return;

    var formatter = new Intl.DateTimeFormat("ro-RO", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    output.textContent = "între " + formatter.format(firstDate) + " și " + formatter.format(secondDate);
  }

  document.querySelectorAll("[data-delivery-window]").forEach(updateDeliveryWindow);

  function variantData(product) {
    var script = product.querySelector("[data-variant-data]");
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  // A sticky action must take the buyer to an unchosen option, never submit
  // the server's first available size. A variant URL remains an explicit choice.
  function requestVariantSelection(product) {
    var controls = Array.prototype.slice.call(product.querySelectorAll("[data-option-index]"));
    var pending = controls.find(function (control) {
      return control.tagName === "SELECT" ? !control.value : !control.querySelector("input:checked");
    });
    if (!pending) return false;
    var target = pending.tagName === "SELECT" ? pending : pending.querySelector("input");
    pending.scrollIntoView({ block: "center", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    if (target) target.focus({ preventScroll: true });
    return true;
  }

  function updateVariant(product) {
    var controls = product.querySelectorAll("[data-option-index]");
    if (!controls.length) return;
    var chosen = Array.prototype.map.call(controls, function (el) {
      if (el.tagName === "SELECT") return el.value;
      var checked = el.querySelector("input:checked");
      return checked ? checked.value : "";
    });
    var pendingChoice = chosen.some(function (value) { return !value; });
    var match = variantData(product).find(function (variant) {
      return variant.options.every(function (value, i) {
        return value === chosen[i];
      });
    });

    var idInput = product.querySelector("[data-variant-id]");
    var priceEls = product.querySelectorAll("[data-price]");
    var button = product.querySelector("[data-add-button]");
    var label = product.querySelector("[data-add-label]");
    var availability = product.querySelector("[data-availability]");
    var availabilityLabel = product.querySelector("[data-availability-label]");
    var saleSummary = product.querySelector("[data-sale-summary]");
    var deliveryWindow = product.querySelector("[data-delivery-window]");
    var shippingNote = product.querySelector("[data-product-shipping]");

    if (idInput) {
      idInput.value = match ? match.id : "";
      idInput.disabled = !match || !match.available;
    }
    var selection = product.querySelector("[data-sticky-selection]");
    if (selection) {
      selection.textContent = match ? Array.prototype.map.call(controls, function (control, index) {
        return (control.dataset.optionName ? control.dataset.optionName + ": " : "") + chosen[index];
      }).join(" / ") : "";
    }
    product.querySelectorAll("[data-sticky-add]").forEach(function (stickyButton) {
      stickyButton.disabled = !pendingChoice && !(match && match.available);
      stickyButton.textContent = pendingChoice ? product.dataset.chooseOptions : (match && match.available ? stickyButton.dataset.add : stickyButton.dataset.soldout);
    });
    product.querySelectorAll("[data-buy-now]").forEach(function (buyButton) {
      buyButton.disabled = !(match && match.available);
    });
    var payment = product.querySelector("[data-product-payment]");
    if (payment) payment.hidden = !(match && match.available);

    // Swap the gallery to the selected variant's image (variant/swatch gallery).
    if (match && match.featured_media_id) {
      var mediaThumb = product.querySelector('[data-media-id="' + match.featured_media_id + '"]');
      if (mediaThumb) activateThumb(product, mediaThumb);
    }

    priceEls.forEach(function (priceEl) {
      if (match && match.on_sale) {
        priceEl.innerHTML =
          '<span class="product__price-sale">' +
          match.price +
          '</span> <s class="product__price-compare">' +
          match.compare_at_price +
          "</s>";
      } else if (match) {
        priceEl.textContent = match.price;
      }
    });

    if (saleSummary && match) {
      saleSummary.hidden = !match.on_sale;
      if (match.on_sale) {
        var percentEl = saleSummary.querySelector("[data-discount-percent]");
        var savingEl = saleSummary.querySelector("[data-discount-saving]");
        if (percentEl) percentEl.textContent = "−" + match.discount_percent + "%";
        if (savingEl) savingEl.textContent = match.savings;
      }
    }

    if (deliveryWindow && match) {
      deliveryWindow.hidden = !match.available;
    }

    if (shippingNote && match) {
      var freeShippingThreshold = Number(shippingNote.dataset.threshold || 20000);
      shippingNote.textContent =
        Number(match.price_cents) >= freeShippingThreshold
          ? shippingNote.dataset.freeShipping
          : shippingNote.dataset.paidShipping;
    }

    // Unit price (e.g. "$0.50/100ml") follows the selected variant.
    var unitPriceEl = product.querySelector("[data-unit-price]");
    if (unitPriceEl && match) {
      if (match.unit_price) {
        var valueEl = unitPriceEl.querySelector("[data-unit-price-value]");
        var baseEl = unitPriceEl.querySelector("[data-unit-price-base]");
        if (valueEl) valueEl.textContent = match.unit_price;
        if (baseEl) baseEl.textContent = (match.unit_price_base || "").replace(/^1\s+/, "");
        unitPriceEl.hidden = false;
      } else {
        unitPriceEl.hidden = true;
      }
    }

    if (button && label) {
      var available = match && match.available;
      button.disabled = !available;
      label.textContent = pendingChoice ? product.dataset.chooseOptions : (available ? label.dataset.add : label.dataset.soldout);
    }

    if (availability && availabilityLabel && match) {
      var isLowStock =
        match.available &&
        match.inventory_management &&
        match.inventory_policy === "deny" &&
        match.inventory_quantity > 0 &&
        match.inventory_quantity <= 5;
      var availabilityState = !match.available ? "soldout" : isLowStock ? "low" : "in";
      var availabilityText = !match.available
        ? availability.dataset.soldOut
        : isLowStock
          ? availability.dataset.lowStock.replace("__COUNT__", match.inventory_quantity)
          : availability.dataset.inStock;

      availability.classList.remove(
        "product__availability--soldout",
        "product__availability--low",
        "product__availability--in"
      );
      availability.classList.add("product__availability--" + availabilityState);
      availabilityLabel.textContent = availabilityText;
    }

    // Rebuild the subscription plan list for the selected variant (plans + prices
    // are per-variant). Preserves the current one-time/subscribe choice.
    var planSelect = product.querySelector("[data-selling-plan]");
    if (planSelect && match) {
      var plans = match.selling_plans || [];
      var previous = planSelect.value;
      planSelect.innerHTML = plans
        .map(function (p) {
          return (
            '<option value="' + p.id + '">' + p.name + " — " + p.price + "</option>"
          );
        })
        .join("");
      if (previous) planSelect.value = previous;
      var subActive = product.querySelector("[data-purchase-subscription]");
      var isSub = subActive && subActive.checked;
      planSelect.hidden = !isSub || !plans.length;
      planSelect.disabled = !isSub || !plans.length;
    }
  }

  // Purchase options: toggle the subscription plan select on/off. When one-time is
  // chosen the select is disabled so `selling_plan` is not submitted at all.
  document.addEventListener("change", function (event) {
    if (!event.target.closest("[data-purchase-options]")) return;
    if (event.target.name !== "purchase_option") return;
    var fieldset = event.target.closest("[data-purchase-options]");
    var planSelect = fieldset.querySelector("[data-selling-plan]");
    if (!planSelect) return;
    var isSub = event.target.value === "subscription";
    var hasPlans = planSelect.options.length > 0;
    planSelect.hidden = !isSub || !hasPlans;
    planSelect.disabled = !isSub || !hasPlans;
  });

  // Country / language selectors submit their localization form on change.
  document.addEventListener("change", function (event) {
    var loc = event.target.closest("[data-localization-select]");
    if (loc && loc.form) {
      loc.form.submit();
      return;
    }
  });

  // Gift-card recipient: reveal the fields + set the "send to recipient" flag.
  document.addEventListener("change", function (event) {
    var toggle = event.target.closest("[data-gift-toggle]");
    if (!toggle) return;
    var wrap = toggle.closest("[data-gift-recipient]");
    if (!wrap) return;
    var fields = wrap.querySelector("[data-gift-fields]");
    var flag = wrap.querySelector("[data-gift-enabled]");
    var offset = wrap.querySelector("[data-gift-offset]");
    if (fields) {
      fields.hidden = !toggle.checked;
      // Disabled inputs don't submit — so unchecking clears any typed recipient
      // properties from the add-to-cart payload.
      fields.querySelectorAll("input:not([data-gift-enabled]), textarea").forEach(function (el) {
        el.disabled = !toggle.checked;
      });
    }
    if (flag) flag.disabled = !toggle.checked;
    if (offset) {
      offset.value = String(new Date().getTimezoneOffset());
      offset.disabled = !toggle.checked;
    }
  });

  document.addEventListener("change", function (event) {
    var control = event.target.closest("[data-option-index]");
    if (!control) return;
    var product = control.closest("[data-product]");
    if (!product) return;
    if (control.classList.contains("product__swatches")) {
      control.querySelectorAll(".product__swatch").forEach(function (labelEl) {
        var input = labelEl.querySelector("input");
        labelEl.classList.toggle("is-selected", !!(input && input.checked));
      });
      var checked = control.querySelector("input:checked");
      var out = control.parentElement.querySelector("[data-swatch-selected]");
      if (out && checked) out.textContent = checked.value;
    }
    updateVariant(product);
  });

  document.querySelectorAll("[data-product][data-choose-options]").forEach(updateVariant);

  function activateThumb(product, thumb) {
    var main = product && product.querySelector("[data-media-main]");
    if (!main || !thumb) return;
    main.src = thumb.getAttribute("data-full");
    main.srcset = thumb.getAttribute("data-srcset");
    var alt = thumb.getAttribute("data-alt");
    if (alt !== null) main.alt = alt;
    var focal = thumb.getAttribute("data-focal");
    main.style.objectPosition = focal ? focal : "";
    product.querySelectorAll("[data-thumb]").forEach(function (t) {
      t.setAttribute("aria-pressed", t === thumb ? "true" : "false");
    });
  }

  document.addEventListener("click", function (event) {
    var thumb = event.target.closest("[data-thumb]");
    if (!thumb) return;
    activateThumb(thumb.closest("[data-product]"), thumb);
  });

  /* ---------- Sticky add-to-cart: show when the main buy button scrolls away, hide over the footer ---------- */
  document.querySelectorAll("[data-product]").forEach(function (product) {
    var sticky = product.querySelector("[data-sticky-atc]");
    var anchor = product.querySelector("[data-add-button]");
    if (!sticky || !anchor || !("IntersectionObserver" in window)) return;
    var footer = document.querySelector(".footer");
    var pastAnchor = false;
    var footerVisible = false;
    function update() {
      sticky.classList.toggle("is-visible", pastAnchor && !footerVisible);
    }
    new IntersectionObserver(
      function (entries) {
        pastAnchor = !entries[0].isIntersecting;
        update();
      },
      { rootMargin: "0px 0px -80px 0px" },
    ).observe(anchor);
    if (footer) {
      new IntersectionObserver(
        function (entries) {
          footerVisible = entries[0].isIntersecting;
          update();
        },
        { rootMargin: "0px 0px 0px 0px" },
      ).observe(footer);
    }
  });

  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-sticky-add]");
    if (!btn || btn.disabled) return;
    var product = btn.closest("[data-product]");
    if (product && requestVariantSelection(product)) return;
    var form = product && product.querySelector("form[data-cart-form]");
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  });

  // Buy now: add the item, then go straight to checkout.
  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-buy-now]");
    if (!btn || btn.disabled) return;
    var product = btn.closest("[data-product]");
    if (product && requestVariantSelection(product)) return;
    var form = product && product.querySelector("form[data-cart-form]");
    if (!form) return;
    btn.setAttribute("aria-busy", "true");
    clearCartError(form);
    var fd = new FormData(form);
    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [formToItem(fd)] }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data && (data.status || data.description)) {
          btn.removeAttribute("aria-busy");
          showCartError(form, data.description || data.message);
          return;
        }
        window.location.href = window.Shopify.routes.root + "checkout";
      })
      .catch(function () {
        btn.removeAttribute("aria-busy");
        showCartError(form);
      });
  });

  /* ---------- Product recommendations (native Shopify API) ---------- */
  document.querySelectorAll("[data-product-recs]").forEach(function (el) {
    var url = el.getAttribute("data-url");
    if (!url) return;
    fetch(url)
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, "text/html");
        var next = parsed.querySelector("[data-product-recs]");
        if (next) el.replaceWith(next);
      })
      .catch(function () {});
  });

  // Add all (frequently bought together): current product + all recommended single-variant items.
  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-add-all]");
    if (!btn) return;
    var scope = btn.closest("[data-product-recs]") || document;
    var ids = [];
    scope.querySelectorAll("form[data-cart-form] [name='id']").forEach(function (input) {
      if (input.value) ids.push(input.value);
    });
    var main = document.querySelector("[data-product] [data-variant-id]");
    if (main && main.value) ids.unshift(main.value);
    if (!ids.length) return;
    btn.setAttribute("aria-busy", "true");
    postCart("cart/add.js", {
      items: ids.map(function (id) {
        return { id: id, quantity: 1 };
      }),
    })
      .then(function (data) {
        if (data && (data.status || data.description)) {
          announce(data.description || data.message || strings.cartError);
          return;
        }
        refreshCartDrawer(data.sections);
        openDialog(document.getElementById(CART_DRAWER_ID));
        announce(strings.cartAdded || "Added to cart");
      })
      .catch(function () {
        announce(strings.cartError || "Couldn't update the cart");
      })
      .finally(function () {
        btn.removeAttribute("aria-busy");
      });
  });

  /* ---------- Recently viewed (localStorage, honest history) ---------- */
  (function () {
    var KEY = "citera:recent";
    function readRecent() {
      try {
        return JSON.parse(localStorage.getItem(KEY)) || [];
      } catch (e) {
        return [];
      }
    }
    function esc(s) {
      var d = document.createElement("div");
      d.textContent = s == null ? "" : s;
      return d.innerHTML;
    }
    // Record the current product.
    var itemEl = document.querySelector("[data-recent-item]");
    if (itemEl) {
      try {
        var item = JSON.parse(itemEl.textContent);
        if (item && item.handle) {
          var list = readRecent().filter(function (p) {
            return p.handle !== item.handle;
          });
          list.unshift(item);
          localStorage.setItem(KEY, JSON.stringify(list.slice(0, 8)));
        }
      } catch (e) {}
    }
    // Render into any recently-viewed section.
    document.querySelectorAll("[data-recently-viewed]").forEach(function (sec) {
      var current = sec.getAttribute("data-current");
      var limit = Number(sec.getAttribute("data-limit")) || 4;
      var grid = sec.querySelector("[data-recently-viewed-grid]");
      if (!grid) return;
      var freeShippingEnabled = sec.getAttribute("data-free-shipping-enabled") === "true";
      var freeShippingThreshold = Number(sec.getAttribute("data-free-shipping-threshold")) || 20000;
      var items = readRecent()
        .filter(function (p) {
          return p.handle !== current;
        })
        .slice(0, limit);
      if (!items.length) return;
      grid.innerHTML = items
        .map(function (p, index) {
          var img = p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : "";
          var ratio = /^(square|portrait|landscape|adapt)$/.test(p.ratio)
            ? p.ratio
            : "portrait";
          var priceCents = Number(p.price_cents) || 0;
          if (!priceCents && typeof p.price === "string") {
            var parsedPrice = p.price
              .replace(/[^\d,.-]/g, "")
              .replace(/\./g, "")
              .replace(",", ".");
            priceCents = Math.round((Number(parsedPrice) || 0) * 100);
          }
          var freeShipping = freeShippingEnabled && priceCents >= freeShippingThreshold;
          var shippingBadge = freeShipping
            ? '<span class="product-card__shipping-badge">🚚 Transport gratuit</span>'
            : "";
          var price = p.on_sale
            ? '<span class="product-card__price-sale">' +
              esc(p.price) +
              '</span><s class="product-card__price-compare">' +
              esc(p.compare_at_price) +
              "</s>"
            : esc(p.price);
          var vendor = p.vendor
            ? '<p class="product-card__vendor">' + esc(p.vendor) + "</p>"
            : "";
          var reviewCount = Math.max(0, Number(p.rating_count) || 0);
          var ratingValue = Math.max(0, Math.min(5, Number(p.rating) || 0));
          var ratingFill = reviewCount > 0 ? Math.round((ratingValue / 5) * 1000) / 10 : 0;
          var reviews = '<div class="product-card__reviews">';
          if (reviewCount > 0) {
            reviews +=
              '<a href="' +
              esc(p.url) +
              '#judgeme_product_reviews" class="product-card__review-link"><span class="product-card__stars" aria-hidden="true"><span class="product-card__stars-base">★★★★★</span><span class="product-card__stars-fill" style="--rating-fill:' +
              ratingFill +
              '%">★★★★★</span></span><strong>' +
              esc(ratingValue.toFixed(1).replace(".", ",")) +
              '</strong><span>(' +
              esc(reviewCount) +
              ")</span></a>";
          } else if (p.judgeme_badge) {
            reviews +=
              '<div class="jdgm-widget jdgm-preview-badge" data-id="' +
              esc(p.id || "") +
              '">' +
              p.judgeme_badge +
              "</div>";
          }
          reviews += "</div>";
          var sequence = String(index + 1).padStart(2, "0");
          return (
            '<li><div class="product-card"><div class="product-card__sequence" aria-hidden="true"><span>' +
            sequence +
            '</span></div><div class="product-card__media-wrap"><a href="' +
            esc(p.url) +
            '" class="product-card__media product-card__media--' +
            ratio +
            '" tabindex="-1" aria-hidden="true">' +
            img +
            "</a>" +
            shippingBadge +
            '</div><div class="product-card__info">' +
            vendor +
            '<h3 class="product-card__title"><a href="' +
            esc(p.url) +
            '">' +
            esc(p.title) +
            "</a></h3>" +
            reviews +
            '<div class="product-card__price">' +
            price +
            "</div></div></div></li>"
          );
        })
        .join("");
      sec.hidden = false;
    });
  })();

  /* ---------- Marquee pause/play (WCAG 2.2.2) ---------- */
  document.addEventListener("click", function (event) {
    var toggle = event.target.closest("[data-marquee-toggle]");
    if (!toggle) return;
    var marquee = toggle.closest(".marquee");
    if (!marquee) return;
    var paused = marquee.classList.toggle("is-paused");
    toggle.setAttribute("aria-pressed", paused ? "true" : "false");
    var label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute("aria-label", label);
  });

  /* ---------- Marquee: fill the viewport so the loop never leaves a gap ---------- */
  (function () {
    var tracks = document.querySelectorAll(".marquee__track");
    if (!tracks.length) return;

    function fill(track) {
      // Drop clones from a previous run before remeasuring.
      track.querySelectorAll("[data-marquee-clone]").forEach(function (clone) {
        clone.remove();
      });
      var groups = track.querySelectorAll(".marquee__group");
      if (!groups.length) return;
      var first = groups[0];
      var groupWidth = first.getBoundingClientRect().width;
      var marquee = track.closest(".marquee");
      var viewport = (marquee || track).getBoundingClientRect().width;
      if (!groupWidth || !viewport) return;
      // Enough groups to always cover the viewport plus one to scroll into.
      var needed = Math.ceil(viewport / groupWidth) + 1;
      var count = track.querySelectorAll(".marquee__group").length;
      while (count < needed) {
        var clone = first.cloneNode(true);
        clone.setAttribute("inert", "");
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("data-marquee-clone", "");
        track.appendChild(clone);
        count++;
      }
      // Shift by exactly one group width => a perfect loop at any width.
      track.style.setProperty("--marquee-shift", "-" + groupWidth + "px");
    }

    function run() {
      tracks.forEach(fill);
    }
    run();

    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(run, 200);
    });
  })();

  /* ---------- Countdown timers (real deadline, no evergreen) ---------- */
  function pad(n) {
    return n < 10 ? "0" + n : String(n);
  }

  function tickCountdown(el) {
    var end = new Date(el.getAttribute("data-countdown-end")).getTime();
    var remaining = end - Date.now();
    var units = el.querySelector("[data-countdown-units]");
    var expired = el.querySelector("[data-countdown-expired]");

    if (isNaN(end)) return true; // bad date -> stop, leave placeholders
    if (remaining <= 0) {
      if (units) units.hidden = true;
      if (expired) expired.hidden = false;
      return true; // done
    }

    var secs = Math.floor(remaining / 1000);
    var days = Math.floor(secs / 86400);
    var hours = Math.floor((secs % 86400) / 3600);
    var mins = Math.floor((secs % 3600) / 60);
    var s = secs % 60;
    set(el, "[data-countdown-days]", days);
    set(el, "[data-countdown-hours]", pad(hours));
    set(el, "[data-countdown-minutes]", pad(mins));
    set(el, "[data-countdown-seconds]", pad(s));
    return false;
  }

  function set(root, sel, val) {
    var node = root.querySelector(sel);
    if (node) node.textContent = val;
  }

  var countdowns = Array.prototype.slice.call(document.querySelectorAll("[data-countdown]"));
  if (countdowns.length) {
    var run = function () {
      countdowns = countdowns.filter(function (el) {
        return !tickCountdown(el);
      });
      if (!countdowns.length && window._citeraCountdown) clearInterval(window._citeraCountdown);
    };
    run();
    window._citeraCountdown = setInterval(run, 1000);
  }

  /* ---------- Quick view (Section Rendering API into a modal) ---------- */
  var qvDialog = document.getElementById("quick-view");
  if (qvDialog) {
    var qvContent = qvDialog.querySelector("[data-quick-view-content]");
    document.addEventListener("click", function (event) {
      var btn = event.target.closest("[data-quick-view]");
      if (!btn) return;
      event.preventDefault();
      var handle = btn.getAttribute("data-quick-view");
      qvContent.innerHTML = "";
      openDialog(qvDialog);
      fetch(window.Shopify.routes.root + "products/" + handle + "?section_id=quick-view")
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var inner = doc.querySelector(".qv");
          qvContent.innerHTML = inner ? inner.outerHTML : html;
        })
        .catch(function () {
          closeDialog(qvDialog);
        });
    });
    // Single-variant selector updates the displayed price.
    qvDialog.addEventListener("change", function (event) {
      var sel = event.target.closest("[data-qv-variant]");
      if (!sel) return;
      var opt = sel.options[sel.selectedIndex];
      var priceEl = qvDialog.querySelector("[data-qv-price]");
      if (opt && priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    });
    // On add-to-cart the global handler opens the cart drawer; close the quick view.
    qvDialog.addEventListener("submit", function () {
      closeDialog(qvDialog);
    });
  }

  /* ---------- Sticky header: fix to top on scroll, with a spacer so content doesn't jump ---------- */
  var stickyHeader = document.querySelector("[data-header-sticky]");
  if (stickyHeader) {
    var headerSpacer = document.createElement("div");
    var headerStuck = false;
    var headerBaseTop = stickyHeader.getBoundingClientRect().top + window.scrollY;
    var updateStuck = function () {
      var catalogMenuOpen = stickyHeader.querySelector(".header__catalog[open]");
      if (catalogMenuOpen) {
        if (headerStuck) {
          stickyHeader.classList.remove("is-stuck");
          if (headerSpacer.parentNode) headerSpacer.parentNode.removeChild(headerSpacer);
          headerStuck = false;
        }
        return;
      }
      if (!headerStuck && window.scrollY > headerBaseTop + 4) {
        headerSpacer.style.height = stickyHeader.offsetHeight + "px";
        stickyHeader.parentNode.insertBefore(headerSpacer, stickyHeader);
        stickyHeader.classList.add("is-stuck");
        headerStuck = true;
      } else if (headerStuck && window.scrollY <= headerBaseTop) {
        stickyHeader.classList.remove("is-stuck");
        if (headerSpacer.parentNode) headerSpacer.parentNode.removeChild(headerSpacer);
        headerStuck = false;
      }
    };
    var catalogMenu = stickyHeader.querySelector(".header__catalog");
    if (catalogMenu) {
      catalogMenu.addEventListener("toggle", function () {
        window.requestAnimationFrame(updateStuck);
      });
    }
    updateStuck();
    window.addEventListener("scroll", updateStuck, { passive: true });
    window.addEventListener(
      "resize",
      function () {
        if (!headerStuck) headerBaseTop = stickyHeader.getBoundingClientRect().top + window.scrollY;
      },
      { passive: true },
    );
  }

  /* ---------- Reveal sections on scroll (respects motion + reduced-motion) ---------- */
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (
    document.body.classList.contains("anim-on") &&
    !reduceMotion &&
    "IntersectionObserver" in window
  ) {
    var reveals = Array.prototype.slice
      .call(document.querySelectorAll("#main-content > .shopify-section"))
      .filter(function (el) {
        // Never hide anything already in the first viewport (protects the LCP
        // section whatever its index — the homepage can lead with a marquee/bar).
        return el.getBoundingClientRect().top >= window.innerHeight;
      });
    reveals.forEach(function (el) {
      el.classList.add("reveal");
    });
    var revealObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add("reveal--in");
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    reveals.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------- Faceted filters: submit on change ---------- */
  document.addEventListener("change", function (event) {
    var form = event.target.closest("[data-facets]");
    if (!form) return;
    if (!(event.target.matches("input") || event.target.matches("select"))) return;
    // Mobile filter drawer collects multiple selections, then submits via "Apply".
    // The sort form (and all desktop filtering) still auto-submits on change.
    if (
      form.hasAttribute("data-facets-filter") &&
      window.matchMedia("(max-width: 749px)").matches
    ) {
      return;
    }
    form.submit();
  });

  /* ---------- Mobile filter drawer: navigate on Apply ----------
     A <form> inside an open modal <dialog> does not reliably submit/navigate on
     iOS Safari. Build the GET URL from the form and navigate explicitly. Handle
     both the click (fires reliably on iOS) and submit (Android / Enter key). */
  function navigateFacets(form) {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (value !== "") params.append(key, value);
    });
    var query = params.toString();
    window.location.assign(query ? location.pathname + "?" + query : location.pathname);
  }
  document.addEventListener("click", function (event) {
    var btn = event.target.closest(".facet-drawer__apply, .facets__apply");
    if (!btn) return;
    var form = btn.closest("form[data-facets-filter]");
    if (!form) return;
    event.preventDefault();
    navigateFacets(form);
  });
  document.addEventListener("submit", function (event) {
    var form = event.target.closest("form[data-facets-filter]");
    if (!form) return;
    event.preventDefault();
    navigateFacets(form);
  });

  /* ---------- Faceted filters: only one dropdown open at a time ---------- */
  // The `toggle` event does not bubble, so listen in the capture phase.
  document.addEventListener(
    "toggle",
    function (event) {
      var facet = event.target;
      if (facet.tagName !== "DETAILS" || !facet.classList.contains("facet") || !facet.open) return;
      var bar = facet.closest(".facets__bar");
      if (!bar) return;
      bar.querySelectorAll("details.facet[open]").forEach(function (other) {
        if (other !== facet) other.open = false;
      });
    },
    true,
  );

  // Close any open facet when clicking outside the filter bar.
  document.addEventListener("click", function (event) {
    if (event.target.closest(".facet")) return;
    document.querySelectorAll("details.facet[open]").forEach(function (facet) {
      facet.open = false;
    });
  });

  /* ---------- Header dropdowns: dismiss on outside-click + Escape ---------- */
  document.addEventListener("click", function (event) {
    if (event.target.closest(".header__dropdown")) return;
    document
      .querySelectorAll("details.header__dropdown[open]")
      .forEach(function (dd) {
        dd.open = false;
      });
  });
  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    var open = document.querySelector("details.header__dropdown[open]");
    if (!open) return;
    open.open = false;
    var summary = open.querySelector("summary");
    if (summary) summary.focus();
  });

  /* ---------- Customer accounts: recover toggle, address forms, country default ---------- */
  (function () {
    function showRecover(name) {
      document.querySelectorAll("[data-recover-panel]").forEach(function (panel) {
        panel.hidden = panel.getAttribute("data-recover-panel") !== name;
      });
    }
    if (
      window.location.hash === "#recover" &&
      document.querySelector('[data-recover-panel="recover"]')
    ) {
      showRecover("recover");
    }

    document.addEventListener("click", function (event) {
      if (event.target.closest("[data-recover-show]")) return showRecover("recover");
      if (event.target.closest("[data-recover-hide]")) return showRecover("login");

      var newToggle = event.target.closest("[data-address-new-toggle]");
      if (newToggle) {
        var box = document.querySelector("[data-address-new]");
        if (box) box.hidden = !box.hidden;
        return;
      }
      var editToggle = event.target.closest("[data-address-edit-toggle]");
      if (editToggle) {
        var form = document.querySelector(
          '[data-address-edit="' + editToggle.getAttribute("data-address-edit-toggle") + '"]',
        );
        if (form) form.hidden = !form.hidden;
        return;
      }
      var del = event.target.closest("[data-address-delete]");
      if (del && del.dataset.confirm && !window.confirm(del.dataset.confirm)) {
        event.preventDefault();
      }
    });

    // Pre-select country selects from their data-default (no legacy Shopify lib).
    document.querySelectorAll("select[data-default]").forEach(function (select) {
      if (select.dataset.default) select.value = select.dataset.default;
    });
  })();

  /* ---------- Third-party app labels: Romanian storefront compatibility ---------- */
  (function () {
    var appText = {
      "Customer Reviews": "Recenzii clienți",
      "Be the first to write a review": "Fii primul care scrie o recenzie",
      "Write a review": "Scrie o recenzie",
      "No items found": "Nu există recenzii încă.",
      "How would you rate this product?": "Cum ai evalua acest produs?",
      "We would love it if you would share a bit about your experience.":
        "Ne-ar ajuta să ne spui câteva lucruri despre experiența ta.",
      Poor: "Slab",
      Great: "Excelent",
    };
    var appLabels = {
      "Close review form": "Închide formularul de recenzie",
      "Review form pages": "Pașii formularului de recenzie",
      Rating: "Evaluare",
      "1 star": "1 stea",
      "Privacy and Terms": "Confidențialitate și termeni",
      "Close modal": "Închide fereastra",
    };

    function translateTextNode(node) {
      var key = node.nodeValue.trim();
      if (!appText[key]) return;
      node.nodeValue = node.nodeValue.replace(key, appText[key]);
    }

    function translateLabel(element) {
      if (!element.getAttribute) return;
      var label = element.getAttribute("aria-label");
      if (!label) return;
      if (element.classList.contains("jdgm-star") && element.dataset.alt) {
        var score = Number(element.dataset.alt);
        var scoreLabel = score + (score === 1 ? " stea" : " stele");
        if (label !== scoreLabel) element.setAttribute("aria-label", scoreLabel);
        if (element.getAttribute("title") !== scoreLabel) {
          element.setAttribute("title", scoreLabel);
        }
      } else if (appLabels[label]) {
        element.setAttribute("aria-label", appLabels[label]);
      } else if (label.indexOf("Quantity:") === 0) {
        element.setAttribute("aria-label", label.replace("Quantity:", "Cantitate:"));
      }
    }

    function localizeAppUi(root) {
      if (root.nodeType === Node.TEXT_NODE) {
        translateTextNode(root);
        return;
      }
      if (root.nodeType !== Node.ELEMENT_NODE) return;

      translateLabel(root);
      root.querySelectorAll("[aria-label]").forEach(translateLabel);

      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) translateTextNode(walker.currentNode);
    }

    localizeAppUi(document.body);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === "characterData") {
          translateTextNode(mutation.target);
          return;
        }
        if (mutation.type === "attributes") {
          translateLabel(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(localizeAppUi);
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["aria-label"],
    });
  })();

  /* ---------- Shopify Forms: delayed discount popup ---------- */
  // Keep the persistent "Ai un cadou aici" teaser out of the shopping flow,
  // then open the existing Shopify Forms offer once, 15 seconds into a visit.
  // The native form still owns eligibility, submission and dismissal states.
  (function setupDiscountPopup() {
    var formHostSelector = "shopify-forms-embed#app-embed-container-996148";
    var sessionKey = "germag-discount-popup-shown";
    var cooldownMs = 7 * 24 * 60 * 60 * 1000;
    var cookieMaxAge = Math.floor(cooldownMs / 1000);
    var delayMs = 15000;
    // Product pages are the buying step: never interrupt them. The 7-day
    // cooldown is not consumed here, so the offer can still open elsewhere.
    var onProductPage = document.body.classList.contains("template-product");
    var attempts = 0;
    var maxAttempts = 80;

    function readCookie() {
      try {
        var prefix = sessionKey + "=";
        var item = document.cookie.split(";").find(function (part) {
          return part.trim().indexOf(prefix) === 0;
        });
        return item ? decodeURIComponent(item.trim().slice(prefix.length)) : "";
      } catch (error) {
        return "";
      }
    }

    function storedValue() {
      var value = "";
      try {
        value = window.localStorage.getItem(sessionKey) || "";
      } catch (error) {
        // Continue with session storage and the first-party cookie fallback.
      }
      if (!value) {
        try {
          value = window.sessionStorage.getItem(sessionKey) || "";
        } catch (error) {
          // Continue with the first-party cookie fallback.
        }
      }
      return value || readCookie();
    }

    function alreadyShown() {
      var value = storedValue();
      if (value === "1") return true; // Preserve the active legacy session.
      var timestamp = Number(value);
      return Number.isFinite(timestamp) && Date.now() - timestamp < cooldownMs;
    }

    function markShown() {
      var value = String(Date.now());
      try {
        window.localStorage.setItem(sessionKey, value);
      } catch (error) {
        // Session storage and the cookie still preserve the dismissal.
      }
      try {
        window.sessionStorage.setItem(sessionKey, value);
      } catch (error) {
        // The first-party cookie still preserves the dismissal.
      }
      try {
        document.cookie =
          sessionKey +
          "=" +
          encodeURIComponent(value) +
          "; path=/; max-age=" +
          cookieMaxAge +
          "; SameSite=Lax; Secure";
      } catch (error) {
        // The popup can still work when browser storage is unavailable.
      }
    }

    function suppressPopup() {
      markShown();
      var host = document.querySelector(formHostSelector);
      if (!host) return;
      host.setAttribute("data-germag-popup-suppressed", "true");
      host.removeAttribute("data-germag-popup-ready");
      host.removeAttribute("data-germag-popup-hold");
    }

    function bindDismissal(root) {
      if (!root || root.__germagDismissalBound) return;
      root.__germagDismissalBound = true;
      root.addEventListener(
        "click",
        function (event) {
          var path = typeof event.composedPath === "function" ? event.composedPath() : [];
          var dismissed = path.some(function (element) {
            return (
              element &&
              element.getAttribute &&
              element.getAttribute("aria-label") === "Close modal"
            );
          });
          if (dismissed) suppressPopup();
        },
        true,
      );
      root.addEventListener(
        "keydown",
        function (event) {
          if (event.key === "Escape") suppressPopup();
        },
        true,
      );
    }

    function hideTeaser(root) {
      if (!root || root.getElementById("germag-hide-form-teaser")) return;
      var style = document.createElement("style");
      style.id = "germag-hide-form-teaser";
      style.textContent =
        ':host { --forms-body-font: normal 400 14px Arial, sans-serif; ' +
        '--forms-heading-font: normal 700 22px Arial, sans-serif; ' +
        '--forms-accent-color: #d6402f; --forms-overlay-background-color: #111111; ' +
        '--forms-overlay-background-opacity: 0.62; --body-text-color: #2a2926; ' +
        '--heading-text-color: #111111; --link-text-color: #b93024; ' +
        '--form-text-color: #161616; --form-placeholder-color: #68645d; ' +
        '--form-background-color: #ffffff; --button-text-color: #ffffff; ' +
        '--button-background-color: #d6402f; --button-close-background-color: #111111; ' +
        '--button-close-color: #ffffff; --container-background-color: #f7f5f0; ' +
        '--form-field-border: 0 0 0 1px #16161633; ' +
        '--form-field-border-focused: 0 0 0 2px #d6402f; ' +
        '--container-border-radius: 16px; --form-field-border-radius: 8px; ' +
        '--button-border-radius: 8px; ' +
        '--container-shadow: 0 28px 80px rgba(0, 0, 0, 0.28); } ' +
        ':host([data-germag-popup-hold="true"]) [data-form-root]:not([data-current-step="tease"]), ' +
        ':host([data-germag-popup-hold="true"]) [role="dialog"], ' +
        ':host([data-germag-popup-suppressed="true"]) [data-form-root] { ' +
        'visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; } ' +
        '[data-sizing="form-wrapper"] { overflow: hidden !important; ' +
        'border: 1px solid rgba(22, 22, 22, 0.18) !important; ' +
        'background-color: #f7f5f0 !important; ' +
        'background-image: linear-gradient(90deg, #111111 0 33.333%, ' +
        '#d6402f 33.333% 66.666%, #f2c94c 66.666% 100%) !important; ' +
        'background-position: top left !important; background-repeat: no-repeat !important; ' +
        'background-size: 100% 5px !important; } ' +
        '[aria-label="Close modal"] { display: inline-flex !important; width: 32px !important; ' +
        'height: 32px !important; align-items: center !important; justify-content: center !important; ' +
        'border-radius: 999px !important; background: #111111 !important; color: #ffffff !important; } ' +
        '[aria-label="Close modal"] svg path { fill: currentColor !important; } ' +
        '[data-testid="form-teaser"] { display: none !important; visibility: hidden !important; } ' +
        '[data-testid="btn-form-submit"] { color: #fff !important; font-weight: 700 !important; ' +
        'letter-spacing: 0.01em !important; box-shadow: none !important; } ' +
        '[data-testid="btn-form-submit"]:hover { background: #b82f22 !important; }';
      root.appendChild(style);
    }

    function consentIsVisible() {
      var candidates = document.querySelectorAll(
        '#shopify-pc__banner, .shopify-pc__banner, [role="alertdialog"]',
      );
      return Array.prototype.some.call(candidates, function (element) {
        return element.getAttribute("aria-hidden") !== "true" && element.getClientRects().length > 0;
      });
    }

    function skinShopButton(root) {
      var capture = root && root.querySelector("shop-lead-capture");
      var shopRoot = capture && capture.shadowRoot;
      if (!shopRoot || shopRoot.getElementById("germag-shop-button-style")) return;

      var style = document.createElement("style");
      style.id = "germag-shop-button-style";
      style.textContent =
        '[data-testid="login-button"] { background: #111111 !important; ' +
        'border-color: #111111 !important; border-radius: 8px !important; ' +
        'box-shadow: none !important; } ' +
        '[data-testid="login-button"]:hover { background: #2c2b28 !important; } ' +
        '[data-testid="login-recognition"] { border-radius: 8px !important; }';
      shopRoot.appendChild(style);
    }

    function polishCopy(root) {
      if (!root) return;
      var heading = root.querySelector("h2");
      if (heading && heading.textContent.trim() === "10% reducere la intreaga comanda") {
        heading.textContent = "10% reducere la întreaga comandă";
      }

      root.querySelectorAll("p").forEach(function (paragraph) {
        if (paragraph.textContent.trim().indexOf("Esti la un email distanta") === 0) {
          paragraph.textContent =
            "Ești la un e-mail distanță de ofertă. Primești reducerea și acces anticipat la promoțiile GERMAG.";
        }
      });

      root.querySelectorAll("label").forEach(function (label) {
        if (label.textContent.trim() === "Email") label.textContent = "Adresa de e-mail";
      });

      var submit = root.querySelector('[data-testid="btn-form-submit"]');
      if (submit && submit.textContent.trim() === "Obtine reducerea") {
        submit.textContent = "Obține reducerea";
      }
    }

    function connect() {
      var host = document.querySelector(formHostSelector);
      var root = host && host.shadowRoot;

      if (host) {
        if (alreadyShown() || onProductPage) {
          host.setAttribute("data-germag-popup-suppressed", "true");
          host.removeAttribute("data-germag-popup-ready");
        } else {
          host.removeAttribute("data-germag-popup-suppressed");
          host.removeAttribute("data-germag-popup-ready");
          host.setAttribute("data-germag-popup-hold", "true");
        }
      }

      if (!root) {
        attempts += 1;
        if (attempts < maxAttempts) window.setTimeout(connect, 250);
        return;
      }

      hideTeaser(root);
      polishCopy(root);
      skinShopButton(root);
      bindDismissal(root);
      new MutationObserver(function () {
        hideTeaser(root);
        polishCopy(root);
        skinShopButton(root);
        bindDismissal(root);
      }).observe(root, { childList: true, subtree: true });

      if (alreadyShown() || onProductPage) return;

      var teaserActivated = false;
      var formReadyAttempts = 0;
      var maxFormReadyAttempts = 50;

      var releasePopup = function () {
        if (consentIsVisible()) {
          window.setTimeout(releasePopup, 500);
          return;
        }

        var currentHost = document.querySelector(formHostSelector);
        var currentRoot = currentHost && currentHost.shadowRoot;
        var formRoot = currentRoot && currentRoot.querySelector('[data-form-root="true"]');
        var teaser = currentRoot && currentRoot.querySelector('[data-testid="form-teaser"]');

        if (!currentHost || !currentRoot || !formRoot) return;

        // Keep every intermediate Shopify Forms step hidden. If Shopify is
        // still on its compact teaser, activate that teaser while the host is
        // held, then reveal only the completed form step. Removing the hold
        // before this transition briefly showed the small teaser underneath
        // the full modal, which looked like two consecutive popups.
        if (formRoot.getAttribute("data-current-step") === "tease") {
          if (!teaserActivated && teaser) {
            teaserActivated = true;
            teaser.click();
          }
          formReadyAttempts += 1;
          if (formReadyAttempts < maxFormReadyAttempts) {
            window.setTimeout(releasePopup, 100);
          }
          return;
        }

        markShown();
        currentHost.setAttribute("data-germag-popup-ready", "true");
        currentHost.removeAttribute("data-germag-popup-hold");
      };

      window.setTimeout(releasePopup, delayMs);
    }

    connect();
  })();

  /* ---------- Back to top ---------- */
  var toTop = document.querySelector("[data-back-to-top]");
  if (toTop) {
    var onTopScroll = function () {
      toTop.hidden = window.scrollY < 600;
    };
    onTopScroll();
    window.addEventListener("scroll", onTopScroll, { passive: true });
    toTop.addEventListener("click", function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    });
  }
})();
